<script setup lang="ts">
/**
 * CommandPalette — Cmd+K / Ctrl+K global navigator (2026-05-29).
 *
 * Modal overlay со списком всех страниц и fuzzy-поиском по названию/aliases.
 * Эмулирует Linear / Notion / Vercel:
 *   - открыть: Cmd+K (Mac) или Ctrl+K (Win/Linux), либо клик на trigger в sidebar
 *   - закрыть: Esc или клик вне модалки
 *   - стрелки ↑/↓ — навигация по результатам
 *   - Enter — перейти на выделенный результат
 *
 * Дополнительно: «Последние» — пять последних посещённых страниц
 * (хранятся в localStorage).
 */
import { computed, nextTick, ref, watch } from "vue";
import { useRouter, useRoute } from "vue-router";
import { usePageRegistry, type PageEntry } from "../../composables/usePageRegistry";
import { useNavFavorites } from "../../composables/useNavFavorites";
import { Search, ArrowRight, Clock, Star } from "lucide-vue-next";

const props = defineProps<{ open: boolean }>();
const emit = defineEmits<{ (e: "close"): void }>();

const router = useRouter();
const route = useRoute();
const { pages, search } = usePageRegistry();
const { favorites } = useNavFavorites();

/** path → PageEntry, for resolving favourites/recents to renderable rows. */
const byPath = computed(() => new Map(pages.value.map((p) => [p.path, p])));

const query = ref("");
const activeIndex = ref(0);
const inputEl = ref<HTMLInputElement | null>(null);

// ─── Recent pages (localStorage) ─────────────────────────────────────────
const RECENT_KEY = "cdp-cmdk-recent-v1";
const recent = ref<string[]>(loadRecent());

function loadRecent(): string[] {
  try {
    const raw = localStorage.getItem(RECENT_KEY);
    return raw ? (JSON.parse(raw) as string[]) : [];
  } catch { return []; }
}

function pushRecent(path: string): void {
  const next = [path, ...recent.value.filter((p) => p !== path)].slice(0, 5);
  recent.value = next;
  try { localStorage.setItem(RECENT_KEY, JSON.stringify(next)); } catch { /* ignore */ }
}

// Track route changes to populate "recent" automatically.
watch(() => route.path, (path) => {
  if (path && path !== "/login") pushRecent(path);
}, { immediate: true });

// ─── Search results ──────────────────────────────────────────────────────
// Favourites (★ pinned) shown first, then recents (excluding favourites).
const favoriteEntries = computed<PageEntry[]>(() => {
  if (query.value.trim()) return [];
  return favorites.value.map((p) => byPath.value.get(p)).filter((p): p is PageEntry => !!p);
});

const recentEntries = computed<PageEntry[]>(() => {
  if (query.value.trim()) return []; // hide recents when typing
  const fav = new Set(favorites.value);
  return recent.value
    .filter((p) => !fav.has(p))
    .map((p) => byPath.value.get(p))
    .filter((p): p is PageEntry => !!p);
});

const results = computed<PageEntry[]>(() => search(query.value, 20));

// Combined list shown to the user — favourites + recent (when empty query) + results.
const visibleList = computed<PageEntry[]>(() => {
  if (query.value.trim()) return results.value;
  const seen = new Set([...favoriteEntries.value, ...recentEntries.value].map((p) => p.path));
  const top = results.value.filter((p) => !seen.has(p.path));
  return [...favoriteEntries.value, ...recentEntries.value, ...top];
});

// ─── Keyboard navigation ─────────────────────────────────────────────────
watch(query, () => { activeIndex.value = 0; });

watch(() => props.open, async (open) => {
  if (open) {
    query.value = "";
    activeIndex.value = 0;
    await nextTick();
    inputEl.value?.focus();
  }
});

function onKeyDown(e: KeyboardEvent): void {
  if (e.key === "Escape") {
    e.preventDefault();
    emit("close");
  } else if (e.key === "ArrowDown") {
    e.preventDefault();
    activeIndex.value = Math.min(activeIndex.value + 1, visibleList.value.length - 1);
    scrollActiveIntoView();
  } else if (e.key === "ArrowUp") {
    e.preventDefault();
    activeIndex.value = Math.max(activeIndex.value - 1, 0);
    scrollActiveIntoView();
  } else if (e.key === "Enter") {
    e.preventDefault();
    const target = visibleList.value[activeIndex.value];
    if (target) go(target.path);
  }
}

function scrollActiveIntoView(): void {
  nextTick(() => {
    document.querySelector("[data-cmdk-active='true']")?.scrollIntoView({ block: "nearest" });
  });
}

function go(path: string): void {
  emit("close");
  if (route.path !== path) router.push(path);
}

// Group headers for display (empty-query mode): Избранное → Недавно → Все разделы.
const favCount = computed(() => favoriteEntries.value.length);
const recCount = computed(() => recentEntries.value.length);
function showFavHeader(idx: number): boolean {
  return !query.value.trim() && favCount.value > 0 && idx === 0;
}
function showRecentHeader(idx: number): boolean {
  return !query.value.trim() && recCount.value > 0 && idx === favCount.value;
}
function showAllHeader(idx: number): boolean {
  const grouped = favCount.value + recCount.value;
  return !query.value.trim() && grouped > 0 && idx === grouped;
}
</script>

<template>
  <Transition
    enter-active-class="transition duration-100 ease-out"
    enter-from-class="opacity-0"
    enter-to-class="opacity-100"
    leave-active-class="transition duration-75 ease-in"
    leave-from-class="opacity-100"
    leave-to-class="opacity-0"
  >
    <div
      v-if="open"
      class="fixed inset-0 z-[100] bg-black/40 backdrop-blur-sm flex items-start justify-center pt-[15vh] px-4"
      data-testid="cmdk-overlay"
      @click.self="emit('close')"
    >
      <div
        class="w-full max-w-xl rounded-xl bg-surface-0 shadow-2xl border border-surface-200 overflow-hidden flex flex-col"
        @keydown="onKeyDown"
      >
        <!-- Search input -->
        <div class="flex items-center gap-2 px-3 py-2.5 border-b border-surface-200">
          <Search class="size-4 text-surface-400 shrink-0" />
          <input
            ref="inputEl"
            v-model="query"
            type="text"
            placeholder="Найти раздел или страницу…"
            class="flex-1 bg-transparent outline-none text-sm text-surface-900 placeholder:text-surface-400"
            data-testid="cmdk-input"
          />
          <kbd class="px-1.5 py-0.5 text-[10px] font-mono bg-surface-100 border border-surface-200 rounded text-surface-400">Esc</kbd>
        </div>

        <!-- Results list -->
        <div class="max-h-[55vh] overflow-y-auto">
          <div v-if="visibleList.length === 0" class="p-6 text-center text-sm text-surface-400">
            Ничего не найдено. Попробуйте другой запрос.
          </div>

          <ul v-else class="py-1.5">
            <template v-for="(item, idx) in visibleList" :key="item.path">
              <!-- "Избранное" header before the pinned block (empty query only) -->
              <li v-if="showFavHeader(idx)" class="px-3 pt-2 pb-1 text-[10px] font-semibold uppercase tracking-wide text-surface-400 flex items-center gap-1.5">
                <Star class="size-3" fill="currentColor" /> Избранное
              </li>
              <!-- "Недавно" header before the recent block -->
              <li v-if="showRecentHeader(idx)" class="px-3 pt-2 pb-1 text-[10px] font-semibold uppercase tracking-wide text-surface-400 flex items-center gap-1.5">
                <Clock class="size-3" /> Недавно
              </li>
              <!-- "Все разделы" header before the rest -->
              <li v-if="showAllHeader(idx)" class="px-3 pt-3 pb-1 mt-1 border-t border-surface-100 text-[10px] font-semibold uppercase tracking-wide text-surface-400">
                Все разделы
              </li>
              <li>
                <button
                  type="button"
                  class="w-full flex items-center justify-between gap-3 px-3 py-2 text-left transition-colors"
                  :class="activeIndex === idx ? 'bg-accent-50 text-accent-900' : 'hover:bg-surface-50 text-surface-800'"
                  :data-cmdk-active="activeIndex === idx"
                  :data-testid="`cmdk-result-${item.path}`"
                  @click="go(item.path)"
                  @mouseenter="activeIndex = idx"
                >
                  <div class="min-w-0 flex-1">
                    <div class="text-sm truncate">{{ item.label }}</div>
                    <div class="text-xs text-surface-400 truncate">{{ item.section }}</div>
                  </div>
                  <ArrowRight v-if="activeIndex === idx" class="size-3.5 text-accent-600 shrink-0" />
                  <code v-else class="text-[10px] font-mono text-surface-300">{{ item.path }}</code>
                </button>
              </li>
            </template>
          </ul>
        </div>

        <!-- Footer with shortcuts hint -->
        <div class="border-t border-surface-200 bg-surface-50 px-3 py-2 flex items-center justify-between text-[11px] text-surface-400">
          <div class="flex items-center gap-3">
            <span class="flex items-center gap-1">
              <kbd class="px-1 py-0.5 bg-surface-0 border border-surface-200 rounded font-mono text-[10px]">↑↓</kbd>
              навигация
            </span>
            <span class="flex items-center gap-1">
              <kbd class="px-1 py-0.5 bg-surface-0 border border-surface-200 rounded font-mono text-[10px]">↵</kbd>
              перейти
            </span>
          </div>
          <span>{{ visibleList.length }} {{ visibleList.length === 1 ? "результат" : "результат(ов)" }}</span>
        </div>
      </div>
    </div>
  </Transition>
</template>
